import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { analyticsAPI, promotionsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Eye, MousePointer, Zap, TrendingUp, ArrowRight, 
  Plus, Activity, BarChart3, Copy, Users, Gift
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const Dashboard = () => {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, promosRes] = await Promise.all([
        analyticsAPI.getOverview(),
        promotionsAPI.getAll()
      ]);
      setAnalytics(analyticsRes.data);
      setPromotions(promosRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-neon-mint border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const stats = [
    { label: 'Total Credits', value: user?.credits || 0, icon: Zap, color: 'mint', change: '+50 welcome' },
    { label: 'Total Views', value: analytics?.total_views || 0, icon: Eye, color: 'mint' },
    { label: 'Total Clicks', value: analytics?.total_clicks || 0, icon: MousePointer, color: 'violet' },
    { label: 'Avg. CTR', value: `${analytics?.avg_ctr || 0}%`, icon: TrendingUp, color: 'amber' },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-neon-mint/10 to-neon-violet/10 border border-white/10 rounded-2xl p-6 lg:p-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-heading font-bold text-2xl lg:text-3xl text-white mb-2">
              Welcome back, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-gray-400">
              {user?.daily_login_claimed 
                ? "You've claimed your daily bonus. Start earning more credits!" 
                : "Don't forget to claim your daily login bonus!"}
            </p>
          </div>
          <div className="flex gap-3">
            <Link to="/dashboard/feed">
              <Button className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold shadow-neon btn-press" data-testid="start-earning-btn">
                <Eye className="w-4 h-4 mr-2" />
                Start Earning
              </Button>
            </Link>
            <Link to="/dashboard/promotions">
              <Button variant="outline" className="border-white/20 text-white hover:bg-white/5">
                <Plus className="w-4 h-4 mr-2" />
                New Promotion
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <Card key={i} className="bg-void-paper border-white/10 card-hover">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  stat.color === 'mint' ? 'bg-neon-mint/10' :
                  stat.color === 'violet' ? 'bg-neon-violet/10' : 'bg-neon-amber/10'
                }`}>
                  <stat.icon className={`w-5 h-5 ${
                    stat.color === 'mint' ? 'text-neon-mint' :
                    stat.color === 'violet' ? 'text-neon-violet' : 'text-neon-amber'
                  }`} />
                </div>
                {stat.change && (
                  <span className="text-xs text-neon-mint bg-neon-mint/10 px-2 py-1 rounded">
                    {stat.change}
                  </span>
                )}
              </div>
              <p className="font-mono font-bold text-3xl text-white mb-1" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                {stat.value}
              </p>
              <p className="text-gray-500 text-sm">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Referral Card */}
      <Card className="bg-gradient-to-r from-neon-violet/10 to-neon-mint/10 border-neon-violet/30">
        <CardContent className="p-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="flex items-center gap-4 flex-1">
              <div className="w-12 h-12 rounded-xl bg-neon-violet/20 flex items-center justify-center">
                <Gift className="w-6 h-6 text-neon-violet" />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-lg text-white">Earn 50 Credits Per Referral!</h3>
                <p className="text-gray-400 text-sm">
                  Share your link and earn credits when friends sign up.
                  {user?.total_referrals > 0 && (
                    <span className="text-neon-mint ml-2">
                      {user.total_referrals} referrals • {user.total_earned || 0} credits earned
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-4 py-2 bg-void rounded-lg border border-white/10">
                <code className="text-neon-mint text-sm">{user?.referral_code || 'N/A'}</code>
              </div>
              <Button
                onClick={() => {
                  const link = `${window.location.origin}/?ref=${user?.referral_code}`;
                  navigator.clipboard.writeText(link);
                  toast.success('Referral link copied!');
                }}
                className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold"
                data-testid="copy-referral-btn"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Views Chart */}
        <Card className="lg:col-span-2 bg-void-paper border-white/10">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-white font-heading flex items-center gap-2">
                <Activity className="w-5 h-5 text-neon-mint" />
                Views Last 7 Days
              </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics?.daily_stats || []}>
                  <defs>
                    <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00E599" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00E599" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis 
                    dataKey="date" 
                    stroke="#525252" 
                    fontSize={12}
                    tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })}
                  />
                  <YAxis stroke="#525252" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#0A0A0A', 
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#00E599" 
                    strokeWidth={2}
                    fill="url(#viewsGradient)" 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-void-paper border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="text-white font-heading flex items-center gap-2">
              <Zap className="w-5 h-5 text-neon-amber" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(analytics?.recent_transactions || []).slice(0, 5).map((tx, i) => (
                <div 
                  key={i} 
                  className="flex items-center justify-between py-2 border-b border-white/5 last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      tx.amount > 0 ? 'bg-neon-mint/10' : 'bg-red-500/10'
                    }`}>
                      <Zap className={`w-4 h-4 ${tx.amount > 0 ? 'text-neon-mint' : 'text-red-400'}`} />
                    </div>
                    <div>
                      <p className="text-white text-sm truncate max-w-[150px]">{tx.description}</p>
                      <p className="text-gray-500 text-xs">
                        {new Date(tx.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <span className={`font-mono font-bold ${tx.amount > 0 ? 'text-neon-mint' : 'text-red-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount}
                  </span>
                </div>
              ))}
              {(!analytics?.recent_transactions || analytics.recent_transactions.length === 0) && (
                <p className="text-gray-500 text-center py-4">No recent activity</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Promotions */}
      <Card className="bg-void-paper border-white/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white font-heading flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-neon-violet" />
              Your Promotions
            </CardTitle>
            <Link to="/dashboard/promotions">
              <Button variant="ghost" className="text-neon-mint hover:text-neon-mint/80">
                View All <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </CardHeader>
        <CardContent>
          {promotions.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-gray-500" />
              </div>
              <p className="text-gray-400 mb-4">No promotions yet</p>
              <Link to="/dashboard/promotions">
                <Button className="bg-neon-mint text-black hover:bg-neon-mint/90 font-bold">
                  Create Your First Promotion
                </Button>
              </Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-gray-500 text-sm border-b border-white/10">
                    <th className="pb-3 font-medium">Promotion</th>
                    <th className="pb-3 font-medium">Views</th>
                    <th className="pb-3 font-medium">Clicks</th>
                    <th className="pb-3 font-medium">CTR</th>
                    <th className="pb-3 font-medium">Budget</th>
                    <th className="pb-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {promotions.slice(0, 5).map((promo) => {
                    const ctr = promo.views > 0 ? (promo.clicks / promo.views * 100).toFixed(1) : 0;
                    return (
                      <tr key={promo.id} className="border-b border-white/5 last:border-0">
                        <td className="py-4">
                          <p className="text-white font-medium truncate max-w-[200px]">{promo.title}</p>
                          <p className="text-gray-500 text-sm">{promo.category}</p>
                        </td>
                        <td className="py-4 font-mono text-white">{promo.views}</td>
                        <td className="py-4 font-mono text-white">{promo.clicks}</td>
                        <td className="py-4 font-mono text-neon-mint">{ctr}%</td>
                        <td className="py-4">
                          <span className="font-mono text-white">{promo.credits_spent}</span>
                          <span className="text-gray-500">/{promo.credit_budget}</span>
                        </td>
                        <td className="py-4">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            promo.is_active 
                              ? 'bg-neon-mint/10 text-neon-mint' 
                              : 'bg-gray-500/10 text-gray-400'
                          }`}>
                            {promo.is_active ? 'Active' : 'Paused'}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
